import { SupportUserAvailabilityOverride } from "@redotech/crdb-enums/support-v2/support-user-availability-override";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useExpiringLocalStorage } from "@redotech/react-util/local-storage";
import AnnotationIcon from "@redotech/redo-icons/annotation.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import CursorClickIcon from "@redotech/redo-icons/cursor-click-01.svg";
import GlobeIcon from "@redotech/redo-icons/globe-02.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import InboxIcon from "@redotech/redo-icons/inbox-01.svg";
import KeyboardIcon from "@redotech/redo-icons/keyboard-01.svg";
import LogOut04 from "@redotech/redo-icons/log-out-04.svg";
import TeamMemberIcon from "@redotech/redo-icons/user-01.svg";
import {
  AuthContext,
  teamAuthStore,
  TOKEN_KEY,
} from "@redotech/redo-merchant-app-common/auth";
import { useRedoEmployeeToolsShortcut } from "@redotech/redo-merchant-app-common/auth/redo-employee-tools";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { useMerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import { useShiftStartNotice } from "@redotech/redo-merchant-app-common/setting/use-shift-start-notice";
import {
  useSetAvailabilityOverrideMutation,
  useShiftStatusQuery,
} from "@redotech/redo-merchant-app-common/setting/use-work-schedule-query";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ConfigureChannelsModal } from "@redotech/redo-merchant-support-app/services/configure-channels-modal";
import { ShiftStartingSoonModal } from "@redotech/redo-merchant-support-app/services/shift-starting-soon-modal";
import { VoiceDeviceStateContext } from "@redotech/redo-merchant-support-app/services/voice-device-state";
import { VoiceDeviceState } from "@redotech/redo-support-common/support/voice/voice-types";
import { WorkScheduleAvailability } from "@redotech/redo-support-common/work-schedule/types";
import { StatusDotColor } from "@redotech/redo-web/arbiter-components/avatar/redo-avatar";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import {
  NavigationUserProfile,
  UserProfileContext,
} from "@redotech/redo-web/arbiter-components/navigation/common/redo-nav-user-profile";
import { DEVICE_AUTHENTICATION_TOKEN_KEY } from "@redotech/redo-web/authentication/authentication-settings";
import { globalShortcuts } from "@redotech/redo-web/keyboard-shortcuts/global-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import {
  VoiceDeviceStateToStatusColor,
  WorkScheduleAvailabilityToStatusColor,
  WorkScheduleAvailabilityToText,
} from "@redotech/redo-web/voice/shared/phone-status";
import { filterTruthy } from "@redotech/util/array";
import { localStore } from "@redotech/web-util/storage";
import { memo, ReactNode, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFeedbackPopover } from "../../app/navigation/top-navigation/feedback-popover-context";
import {
  MerchantLocaleSelector,
  useMerchantLanguageLabel,
  useMerchantLocaleSelectorVisible,
} from "../../app/navigation/top-navigation/merchant-locale-selector";
import { KeyboardShortcutsModal } from "../../keyboard-shortcuts/keyboard-shortcuts-modal";
import { autoOnboardingUrl } from "../../onboarding";
import { WALKTHROUGH_REGISTRY } from "../../walkthrough/walkthrough-registry";
import { getLogoutDestination } from "./logout-destination";

const HELP_ARTICLES_URL = "https://help.getredo.com/en/";

const PICKER_WALKTHROUGHS = WALKTHROUGH_REGISTRY.filter(
  (entry) => !entry.hideFromPicker,
);

export const UserProfileService = memo(function UserProfileService({
  children,
}: {
  children: ReactNode;
}) {
  const user = useRequiredContext(UserContext);

  const team = useRequiredContext(TeamContext);

  const auth = useContext(AuthContext);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const showLanguage = useMerchantLocaleSelectorVisible();
  const languageLabel = useMerchantLanguageLabel();

  // Availability swaps this menu's contents rather than opening a nested
  // flyout, so the choices land where the eye already is.
  const [profileMenuView, setProfileMenuView] = useState<
    "profile" | "availability"
  >("profile");

  const { availabilityStatus } = useRequiredContext(VoiceDeviceStateContext);

  const [walkthroughPickerOpen, setWalkthroughPickerOpen] = useState(false);

  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);

  const { open: openFeedback } = useFeedbackPopover();

  useKeyboardShortcuts({
    bindings: [
      {
        shortcut: globalShortcuts.OPEN_KEYBOARD_SHORTCUTS,
        handler: () => setKeyboardShortcutsOpen(true),
      },
    ],
    scope: "global",
  });
  useRedoEmployeeToolsShortcut();

  const [configureChannelsOpen, setConfigureChannelsOpen] = useState(false);

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const routes = useMerchantRoutes();

  const supportEnabled = !!team?.settings.support?.enabled;

  const shiftStatusQuery = useShiftStatusQuery({ enabled: supportEnabled });
  const shiftStartNotice = useShiftStartNotice({ enabled: supportEnabled });
  const setAvailabilityOverride = useSetAvailabilityOverrideMutation();
  const reportedAvailability = shiftStatusQuery.data?.availability;
  const shiftActive = shiftStatusQuery.data?.active;
  /** An API that predates `availability` still reports the two-state `active`. */
  const shiftAvailability: WorkScheduleAvailability | undefined =
    reportedAvailability ??
    (shiftActive === undefined
      ? undefined
      : shiftActive
        ? WorkScheduleAvailability.AVAILABLE
        : WorkScheduleAvailability.OFFLINE);

  const settingsUrl = routes.getSettingsSubRoute("personal/profile");

  const navigate = useNavigate();

  const [deviceAuthToken] = useExpiringLocalStorage<string | undefined>(
    DEVICE_AUTHENTICATION_TOKEN_KEY(team._id),
    undefined,
  );

  const handleLogout = useHandler(() => {
    if (team) {
      teamAuthStore(team._id).set(undefined);
      localStore(TOKEN_KEY).set(undefined);
    } else {
      localStorage.clear();
    }
    location.href = getLogoutDestination({
      deviceAuthToken,
      origin: location.origin,
      team,
    });
  });

  const profileImageUrl = useMemo(() => {
    return getUserAvatarUrl({
      email: user.email || "",
      size: 48,
      userId: user._id,
    });
  }, [user]);

  const dropdownItems: RedoListItem<() => void>[] = useMemo(() => {
    // A manual Available choice lasts until the next shift boundary, so
    // there is no separate clear action. Busy still counts as Available.
    const choseActive =
      shiftAvailability !== undefined &&
      shiftAvailability !== WorkScheduleAvailability.OFFLINE;

    const availabilityItem: RedoListItem<() => void> | undefined =
      supportEnabled && shiftAvailability !== undefined
        ? {
            id: "availability",
            type: "text",
            // Shows the state, so it reads without being opened.
            text: WorkScheduleAvailabilityToText[shiftAvailability],
            TrailingIcon: ArrowRightIcon,
            leadingItem: {
              type: "status",
              color: WorkScheduleAvailabilityToStatusColor[shiftAvailability],
            },
            value: () => {
              setProfileMenuView("availability");
              // The dropdown closes on every select and value() runs after that
              // close, so this keeps the panel up instead of dismissing it.
              setProfileDropdownOpen(true);
            },
          }
        : undefined;

    const availabilityViewItems: RedoListItem<() => void>[] = [
      {
        id: "shift-available",
        type: "text",
        text: "Available",
        supportingText: "New tickets can be assigned",
        leadingItem: {
          type: "status",
          color:
            WorkScheduleAvailabilityToStatusColor[
              WorkScheduleAvailability.AVAILABLE
            ],
        },
        TrailingIcon: choseActive ? CheckIcon : undefined,
        value: () => {
          setAvailabilityOverride.mutate(
            SupportUserAvailabilityOverride.Active,
          );
          setProfileDropdownOpen(false);
        },
      },
      {
        id: "shift-offline",
        type: "text",
        text: "Unavailable",
        supportingText: "Pause automatic assignments",
        leadingItem: {
          type: "status",
          color:
            WorkScheduleAvailabilityToStatusColor[
              WorkScheduleAvailability.OFFLINE
            ],
        },
        TrailingIcon: !choseActive ? CheckIcon : undefined,
        value: () => {
          setAvailabilityOverride.mutate(
            SupportUserAvailabilityOverride.Inactive,
          );
          setProfileDropdownOpen(false);
        },
      },
      {
        id: "availability-divider",
        type: "custom",
        render: <Divider />,
        useWrapper: false,
        value: () => undefined,
      },
      {
        id: "configure-channels",
        type: "text",
        text: "Configure channels",
        leadingItem: { type: "icon", Icon: InboxIcon },
        value: () => {
          setProfileDropdownOpen(false);
          setConfigureChannelsOpen(true);
        },
      },
    ];

    if (profileMenuView === "availability") {
      return availabilityViewItems;
    }

    const profileItem: RedoListItem<() => void> = {
      id: "profile",
      type: "text",
      text: "Profile",
      leadingItem: { type: "icon", Icon: TeamMemberIcon },
      value: () => {
        navigate(settingsUrl);
      },
    };

    const onboardingItem: RedoListItem<() => void> | undefined = auth
      ? {
          id: "onboarding",
          type: "text",
          href: autoOnboardingUrl(auth.token, auth.teamId).toString(),
          text: "Onboarding",
          leadingItem: { type: "icon", Icon: ArrowRightIcon },
          value: () => {
            location.href = autoOnboardingUrl(
              auth.token,
              auth.teamId,
            ).toString();
          },
        }
      : undefined;

    const walkthroughItem: RedoListItem<() => void> | undefined =
      PICKER_WALKTHROUGHS.length > 0
        ? {
            id: "walkthrough",
            type: "text",
            text: "Walkthrough",
            leadingItem: { type: "icon", Icon: CursorClickIcon },
            value: () => {
              setProfileDropdownOpen(false);
              setWalkthroughPickerOpen(true);
            },
          }
        : undefined;

    const helpArticlesItem: RedoListItem<() => void> = {
      id: "help-articles",
      type: "text",
      href: HELP_ARTICLES_URL,
      text: "Help articles",
      leadingItem: { type: "icon", Icon: HelpCircleIcon },
      value: () =>
        window.open(HELP_ARTICLES_URL, "_blank", "noopener,noreferrer"),
    };

    const keyboardShortcutsItem: RedoListItem<() => void> = {
      id: "keyboard-shortcuts",
      type: "text",
      text: "Keyboard shortcuts",
      leadingItem: { type: "icon", Icon: KeyboardIcon },
      value: () => {
        setProfileDropdownOpen(false);
        setKeyboardShortcutsOpen(true);
      },
    };

    const giveFeedbackItem: RedoListItem<() => void> = {
      id: "give-feedback",
      type: "text",
      text: "Give feedback",
      leadingItem: { type: "icon", Icon: AnnotationIcon },
      value: () => {
        setProfileDropdownOpen(false);
        openFeedback();
      },
    };

    const languageItem: RedoListItem<() => void> | undefined = showLanguage
      ? {
          id: "language",
          type: "text",
          text: languageLabel,
          leadingItem: { type: "icon", Icon: GlobeIcon },
          value: () => {
            setProfileDropdownOpen(false);
            setLanguageOpen(true);
          },
        }
      : undefined;

    const logoutItem: RedoListItem<() => void> = {
      id: "logout",
      type: "text",
      text: "Logout",
      leadingItem: { type: "icon", Icon: LogOut04 },
      value: handleLogout,
    };

    return filterTruthy([
      availabilityItem,
      profileItem,
      onboardingItem,
      walkthroughItem,
      helpArticlesItem,
      keyboardShortcutsItem,
      giveFeedbackItem,
      languageItem,
      logoutItem,
    ]);
  }, [
    auth,
    handleLogout,
    languageLabel,
    navigate,
    openFeedback,
    settingsUrl,
    showLanguage,
    supportEnabled,
    shiftAvailability,
    setAvailabilityOverride,
    profileMenuView,
  ]);

  const walkthroughItems: RedoListItem<string>[] = useMemo(() => {
    return PICKER_WALKTHROUGHS.map((entry) => ({
      id: entry.id,
      type: "text" as const,
      text: entry.name,
      value: entry.id,
      ...(entry.Icon && {
        leadingItem: { type: "icon" as const, Icon: entry.Icon },
      }),
    }));
  }, []);

  // The device knows about a call before the API does, but busy only downgrades:
  // someone already offline stays offline rather than reading as briefly busy.
  const localAvailability =
    shiftAvailability === WorkScheduleAvailability.AVAILABLE &&
    availabilityStatus === VoiceDeviceState.IN_CALL
      ? WorkScheduleAvailability.BUSY
      : shiftAvailability;

  const presenceDotColor: StatusDotColor =
    supportEnabled && localAvailability !== undefined
      ? WorkScheduleAvailabilityToStatusColor[localAvailability]
      : VoiceDeviceStateToStatusColor[availabilityStatus];

  const profileData: NavigationUserProfile = {
    name: user.name,
    teamName: team.name,
    setDropdownAnchor,
    setProfileDropdownOpen,
    profileImageUrl,
    status: presenceDotColor,
    profileDropdownOpen,
  };

  return (
    <UserProfileContext.Provider value={profileData}>
      {configureChannelsOpen && (
        <ConfigureChannelsModal
          onClose={() => setConfigureChannelsOpen(false)}
        />
      )}
      {shiftStartNotice.startsAt && (
        <ShiftStartingSoonModal
          onClose={shiftStartNotice.dismiss}
          startsAt={shiftStartNotice.startsAt}
        />
      )}
      <RedoListDropdown<() => void>
        darkMode
        disabledItems={["availability-divider"]}
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={profileDropdownOpen}
        items={dropdownItems}
        itemSelected={({ item }) => {
          item.value();
        }}
        setDropdownOpen={(open) => {
          setProfileDropdownOpen(open);
          // Reopen on the profile panel rather than wherever they left off.
          if (!open) {
            setProfileMenuView("profile");
          }
        }}
        size="sm"
      />
      <RedoListDropdown<string>
        darkMode
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={walkthroughPickerOpen}
        items={walkthroughItems}
        itemSelected={({ item }) => {
          navigate(
            `/stores/${team._id}/home/overview?walkthrough=${item.value}`,
          );
        }}
        setDropdownOpen={setWalkthroughPickerOpen}
        size="sm"
      />
      <KeyboardShortcutsModal
        isOpen={keyboardShortcutsOpen}
        onClose={() => setKeyboardShortcutsOpen(false)}
      />
      <MerchantLocaleSelector
        onOpenChange={setLanguageOpen}
        open={languageOpen}
      />
      {children}
    </UserProfileContext.Provider>
  );
});
